import { useState, useEffect, useRef, useCallback } from "react";
import { useTheme } from "../components/ThemeProvider";
import { dialogs } from "../context/DialogContext";
import usePermiso from "../hooks/usePermiso";

const API = import.meta.env.VITE_API_URL || "http://localhost:4000/api";

const wa = {
  green:       "#16a34a",
  greenFrom:   "#15803d",
  greenTo:     "#16a34a",
  greenLight:  "#22c55e",
  greenText:   "#bbf7d0",
  greenShadow: "rgba(22,163,74,0.35)",
  greenBg:     "rgba(22,163,74,0.08)",
  greenBorder: "rgba(22,163,74,0.20)",
  chipText:    "#4ade80",
  redBg:       "rgba(239,68,68,0.08)",
  redBorder:   "rgba(239,68,68,0.25)",
  redText:     "#ef4444",
  amberBg:     "rgba(245,158,11,0.08)",
  amberBorder: "rgba(245,158,11,0.25)",
  amberText:   "#f59e0b",
};

function StatusBadge({ status, phone, qrBase64, reconnecting, desvinculada }) {
  const configs = {
    connected:    { color: wa.green,     bg: wa.greenBg,  border: wa.greenBorder, dot: wa.green,     label: phone ? `Conectado · +${phone}` : "Conectado" },
    qr_ready:     { color: wa.amberText, bg: wa.amberBg,  border: wa.amberBorder, dot: wa.amberText, label: "Esperando escaneo del QR" },
    connecting:   { color: wa.amberText, bg: wa.amberBg,  border: wa.amberBorder, dot: wa.amberText, label: "Conectando..." },
    disconnected: { color: wa.redText,   bg: wa.redBg,    border: wa.redBorder,   dot: wa.redText,   label: "Desconectado" },
    saved:        { color: wa.amberText, bg: wa.amberBg,  border: wa.amberBorder, dot: wa.amberText, label: "Sesión guardada — reconectando" },
    not_started:  { color: wa.redText,   bg: wa.redBg,    border: wa.redBorder,   dot: wa.redText,   label: "Sin conexión" },
  };
  let label = configs[status]?.label || configs.not_started.label;
  if (desvinculada) label = "WhatsApp desvinculado — hay que volver a vincular este dispositivo";
  else if (qrBase64) label = "Escanea el QR";
  else if (reconnecting || status === "saved") label = "Reconectando sesión guardada…";
  const cfg = desvinculada ? configs.disconnected : (configs[status] || configs.not_started);
  const pulsing = !desvinculada && (reconnecting || ["connecting", "qr_ready", "saved"].includes(status));
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 14px", borderRadius: 20, background: cfg.bg, border: `1px solid ${cfg.border}` }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: cfg.dot, flexShrink: 0, animation: pulsing ? "wa-pulse 1.4s ease-in-out infinite" : "none" }} />
      <span style={{ fontSize: 13, fontWeight: 600, color: cfg.color }}>{label}</span>
    </div>
  );
}

function aplicarEstadoServidor(d, setters, { keepConnecting = false } = {}) {
  const {
    setStatus, setPhone, setQrBase64, setServerError, setLastDisconnect, setStuckConnecting,
    setConnectingSince, setNeedsQrScan, setAnaPhoneConflict, setAnaWaPhoneReserved, setManualQrMode,
    setConectando, setYieldForQrPause, setHasSavedOnDisk, setReconnectingFromSaved, setStatusMessage,
    setQrPreparando,
  } = setters;
  const serverStatus = d.status;
  const yieldPause = Boolean(d.yieldForQrPause);
  // Chromium tarda ~90s en dar el primer QR: es espera normal, no un fallo.
  const preparandoQr = Boolean(d.qrPreparando);
  const errTxt = `${d.error || ""} ${d.lastDisconnectReason || ""}`;
  const falloTerminal =
    !yieldPause &&
    !preparandoQr &&
    /Max qrcode|localauth_dead|ya no es válida|auth_failure|LOGOUT|chrome_eagain(?!_retry)|chrome_launch_failed|Failed to launch|Resource temporarily unavailable|pthread_create|Chromium no arrancó|saturado\. Ya he liberado|desvinculada|WhatsApp desvinculado/i.test(
      errTxt
    );
  // No mantener "connecting" fantasma si el servidor ya falló (Angel: Max QR sin botón).
  if (falloTerminal && !d.qrBase64 && serverStatus !== "connected") {
    setStatus(serverStatus === "qr_ready" && !d.qrBase64 ? "disconnected" : (serverStatus === "connecting" ? "disconnected" : serverStatus));
    if (setConectando) setConectando(false);
  } else if (!keepConnecting || !["saved", "not_started"].includes(serverStatus)) {
    setStatus(serverStatus);
  }
  setPhone(d.phone);
  // No borrar el QR de la UI en parpadeos "connecting" (parece que se va y vuelve;
  // el móvil ya estaba vinculando y falla).
  if (d.qrBase64) {
    setQrBase64(d.qrBase64);
  } else if (["connected", "disconnected", "not_started"].includes(serverStatus) || falloTerminal) {
    setQrBase64(null);
  }
  setServerError(d.error || "");
  setLastDisconnect(d.lastDisconnectReason || "");
  if (setYieldForQrPause) setYieldForQrPause(yieldPause);
  if (setHasSavedOnDisk) setHasSavedOnDisk(Boolean(d.hasSavedOnDisk));
  if (setReconnectingFromSaved) setReconnectingFromSaved(Boolean(d.reconnectingFromSaved));
  if (setStatusMessage) setStatusMessage(d.statusMessage || "");
  if (setNeedsQrScan) setNeedsQrScan(Boolean(d.needsQrScan));
  if (setAnaPhoneConflict) setAnaPhoneConflict(Boolean(d.anaPhoneConflict));
  if (setAnaWaPhoneReserved) setAnaWaPhoneReserved(d.anaWaPhoneReserved || null);
  if (setManualQrMode) setManualQrMode(Boolean(d.manualQrMode));
  if (setQrPreparando) setQrPreparando(preparandoQr);
  if (setStuckConnecting) setStuckConnecting(!preparandoQr && (Boolean(d.stuckConnecting) || (falloTerminal && !d.qrBase64)));
  if (setConnectingSince) {
    if (falloTerminal && !d.qrBase64) {
      setConnectingSince(null);
    } else if (["connecting", "saved"].includes(serverStatus) || keepConnecting) {
      setConnectingSince((prev) => prev || (d.connectingSince ? new Date(d.connectingSince).getTime() : Date.now()));
    } else {
      setConnectingSince(null);
    }
  }
}

export default function ConexionWhatsApp() {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const veEstado = puede("cfg_wa_estado", "ver");
  const veVincular = puede("cfg_wa_vincular", "ver");
  const veDesconectar = puede("cfg_wa_desconectar", "ver");

  const [status, setStatus]         = useState("not_started");
  const [qrBase64, setQrBase64]     = useState(null);
  const [phone, setPhone]           = useState(null);
  const [cargando, setCargando]     = useState(true);
  const [accion, setAccion]         = useState(false);
  const [conectando, setConectando] = useState(false);
  const [error, setError]           = useState("");
  const [serverError, setServerError] = useState("");
  const [lastDisconnect, setLastDisconnect] = useState("");
  const [diag, setDiag]             = useState(null);
  const [mostrarDiag, setMostrarDiag] = useState(false);
  const [stuckConnecting, setStuckConnecting] = useState(false);
  const [connectingSince, setConnectingSince] = useState(null);
  const [needsQrScan, setNeedsQrScan] = useState(false);
  const [anaPhoneConflict, setAnaPhoneConflict] = useState(false);
  const [anaWaPhoneReserved, setAnaWaPhoneReserved] = useState(null);
  const [manualQrMode, setManualQrMode] = useState(false);
  const [qrPreparando, setQrPreparando] = useState(false);
  const [yieldForQrPause, setYieldForQrPause] = useState(false);
  const [hasSavedOnDisk, setHasSavedOnDisk] = useState(false);
  const [reconnectingFromSaved, setReconnectingFromSaved] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const pollRef = useRef(null);
  const autoConnectRef = useRef(false);
  const conectandoRef = useRef(false);
  const token   = localStorage.getItem("token");

  const setters = {
    setStatus, setPhone, setQrBase64, setServerError, setLastDisconnect, setStuckConnecting,
    setConnectingSince, setNeedsQrScan, setAnaPhoneConflict, setAnaWaPhoneReserved, setManualQrMode,
    setConectando, setYieldForQrPause, setHasSavedOnDisk, setReconnectingFromSaved, setStatusMessage,
    setQrPreparando,
  };

  conectandoRef.current = conectando;

  const refrescarEstado = useCallback(async (opts = {}) => {
    const res = await fetch(`${API}/whatsapp/estado`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: "no-store",
    });
    const data = await res.json();
    if (!data.ok) return null;
    aplicarEstadoServidor(data.data, setters, {
      keepConnecting: opts.keepConnecting ?? conectandoRef.current,
    });
    return data.data;
  }, [token]);

  const iniciarPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const d = await refrescarEstado();
        if (!d) return;
        if (["connected", "qr_ready", "disconnected", "not_started"].includes(d.status)) {
          setConectando(false);
          setCargando(false);
        } else if (d.status === "connecting") {
          setStatus("connecting");
        }
      } catch { /* ignorar */ }
    }, 800);
  };

  const handleConectar = async ({ forzarQR = false } = {}) => {
    setAccion(true);
    setError("");
    setConectando(true);
    const esSavedReconnect =
      !forzarQR &&
      (hasSavedOnDisk || yieldForQrPause || reconnectingFromSaved || lastDisconnect === "yield_for_qr" || phone);
    setStatus(esSavedReconnect ? "saved" : "connecting");
    setConnectingSince(Date.now());
    setStuckConnecting(false);
    try {
      const res = await fetch(`${API}/whatsapp/conectar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ forzar_qr: forzarQR }),
        cache: "no-store",
      });
      const data = await res.json();
      if (!data.ok) {
        setError(data.error || "Error al conectar");
        setConectando(false);
        const d = await refrescarEstado({ keepConnecting: false });
        if (d?.status) setStatus(d.status);
      } else {
        await refrescarEstado({ keepConnecting: true });
      }
    } catch {
      setError("No se pudo conectar con el servidor");
      setConectando(false);
      setStatus("not_started");
    }
    setAccion(false);
  };

  const handleNuevoQR = async () => {
    const ok = await dialogs.confirm(
      "Se borrará la sesión guardada, se abortará cualquier reconexión automática y se generará un QR nuevo legible. ¿Continuar?"
    );
    if (!ok) return;
    setManualQrMode(true);
    setError("");
    setServerError("");
    setLastDisconnect("manual_qr");
    setStatus("connecting");
    setQrBase64(null);
    setConectando(true);
    setStuckConnecting(false);
    setConnectingSince(Date.now());
    autoConnectRef.current = false;
    await handleConectar({ forzarQR: true });
  };

  const cargarDiagnostico = async () => {
    try {
      const res = await fetch(`${API}/whatsapp/diagnostico`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.ok) setDiag(data.data?.sesion || data.data);
    } catch {
      setDiag(null);
    }
  };

  useEffect(() => {
    refrescarEstado().finally(() => setCargando(false));
    iniciarPolling();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [refrescarEstado]);

  // Contador visible mientras conecta (para detectar atasco en UI)
  const [, uiTick] = useState(0);
  useEffect(() => {
    if (status !== "connecting" && status !== "saved" && !conectando) return undefined;
    const id = setInterval(() => uiTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [status, conectando]);

  // Sesión en disco pero Chromium no arrancado → iniciar conexión automáticamente (una vez).
  // Nunca con QR en pantalla, modo manual, desvinculada/LOGOUT ni fallo que exige escanear.
  useEffect(() => {
    if (cargando || accion || conectando || anaPhoneConflict || manualQrMode) return;
    if (qrBase64 || needsQrScan || qrPreparando) return;
    if (
      /Max qrcode|localauth_dead|manual_qr|LOGOUT|auth_failure|desvinculada/i.test(
        `${lastDisconnect} ${serverError}`
      )
    ) {
      return;
    }
    if (status !== "saved" || autoConnectRef.current) return;
    if (yieldForQrPause || lastDisconnect === "chrome_queue_wait") return;
    autoConnectRef.current = true;
    handleConectar({ forzarQR: false });
  }, [
    status,
    cargando,
    accion,
    conectando,
    anaPhoneConflict,
    manualQrMode,
    lastDisconnect,
    serverError,
    yieldForQrPause,
    qrBase64,
    needsQrScan,
    qrPreparando,
  ]);

  // LOGOUT / desvinculada: no auto-forzarQR. Aviso al operador + botón supervisado.

  const handleDesconectar = async () => {
    if (!(await dialogs.confirm("¿Seguro que quieres desconectar tu WhatsApp? Tendrás que volver a escanear el QR."))) return;
    setAccion(true);
    setError("");
    autoConnectRef.current = false;
    try {
      const res = await fetch(`${API}/whatsapp/desconectar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.ok) setError(data.error || "Error al desconectar");
      else { setStatus("not_started"); setQrBase64(null); setPhone(null); setServerError(""); }
    } catch {
      setError("No se pudo conectar con el servidor");
    }
    setAccion(false);
  };

  const sesionDesvinculada =
    lastDisconnect === "desvinculada" ||
    lastDisconnect === "desvinculada_logout" ||
    lastDisconnect === "LOGOUT" ||
    /WhatsApp desvinculado/i.test(`${error || ""} ${serverError || ""}`);
  const esReconexionGuardada =
    !sesionDesvinculada &&
    !manualQrMode &&
    !qrBase64 &&
    !needsQrScan &&
    (yieldForQrPause ||
      reconnectingFromSaved ||
      hasSavedOnDisk ||
      lastDisconnect === "yield_for_qr" ||
      lastDisconnect === "chrome_eagain_retry" ||
      lastDisconnect === "chrome_queue_wait" ||
      status === "saved" ||
      (hasSavedOnDisk && Boolean(phone)));
  const mensajeErrorCrudo = error || serverError;
  const mensajeErrorVisible =
    esReconexionGuardada &&
    /yield_for_qr|pausó un momento|Reconectando automáticamente|sesión guardada|El servidor está ocupado|reconectando tu sesión guardada/i.test(
      `${mensajeErrorCrudo} ${statusMessage}`
    )
      ? error
      : mensajeErrorCrudo;
  // Fallos que ya no van a "arreglarse solos" esperando el spinner.
  const falloNecesitaQrManual =
    !esReconexionGuardada &&
    !qrPreparando &&
    /Max qrcode|localauth_dead|ya no es válida|Generar nuevo QR|chrome_launch_failed|chrome_eagain(?!_retry)|auth_failure|LOGOUT|manual_qr|Failed to launch|Resource temporarily unavailable|pthread_create|Chromium no arrancó|saturado\. Ya he liberado|desvinculada|WhatsApp desvinculado/i.test(
      `${mensajeErrorVisible || ""} ${lastDisconnect || ""}`
    );
  const qrListoSinImagen =
    !qrBase64 &&
    !qrPreparando &&
    !sesionDesvinculada &&
    (status === "qr_ready" || needsQrScan);
  const segundosConectando = connectingSince ? Math.floor((Date.now() - connectingSince) / 1000) : 0;
  // Mientras se prepara el QR (~90s) no hay nada atascado: pulsar otra vez reinicia la espera.
  const conexionAtascada = qrPreparando
    ? false
    : esReconexionGuardada
    ? stuckConnecting || segundosConectando >= 90
    : stuckConnecting || segundosConectando >= 30 || falloNecesitaQrManual;
  // Autonomía: si no estás connected y no hay QR en pantalla, SIEMPRE puedes generar QR.
  const puedeGenerarQr = veVincular && status !== "connected" && !qrBase64 && !qrPreparando;
  const mostrarSpinner =
    !qrBase64 &&
    !falloNecesitaQrManual &&
    (qrPreparando || status === "connecting" || status === "saved" || (conectando && !qrBase64));
  const mostrarBotonesReconexion =
    puedeGenerarQr &&
    !mostrarSpinner &&
    (["not_started", "disconnected"].includes(status) ||
      anaPhoneConflict ||
      qrListoSinImagen ||
      falloNecesitaQrManual ||
      conexionAtascada);

  return (
    <div style={{ minHeight: "100vh", background: c.bgPage || c.bg, padding: "32px 24px", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @keyframes wa-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(.85)} }
        @keyframes wa-spin  { to{transform:rotate(360deg)} }
      `}</style>

      <div style={{ maxWidth: 600, margin: "0 auto" }}>

        <div style={{ marginBottom: 28 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: c.textPrimary, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 40, height: 40, borderRadius: 10, background: `linear-gradient(135deg,${wa.greenFrom},${wa.greenTo})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0, boxShadow: `0 4px 12px ${wa.greenShadow}` }}>💬</span>
            Conexión WhatsApp
          </h1>
          <p style={{ margin: "8px 0 0 50px", fontSize: 13.5, color: c.textMuted, fontWeight: 500 }}>
            Conecta tu WhatsApp personal para enviar mensajes desde el CRM.
          </p>
        </div>

        <div style={{ background: c.bgCard, borderRadius: 14, border: `1px solid ${c.border}`, overflow: "hidden", boxShadow: isDark ? "0 4px 24px rgba(0,0,0,.3)" : "0 4px 24px rgba(0,0,0,.06)" }}>
          <div style={{ height: 3, background: `linear-gradient(90deg,${wa.greenLight},${wa.green},${wa.greenFrom},${wa.green},${wa.greenLight})` }} />

          <div style={{ padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${c.border}` }}>
            {veEstado && (
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary, marginBottom: 6 }}>Estado de tu sesión</div>
              {cargando ? <div style={{ fontSize: 13, color: c.textMuted }}>Cargando...</div> : <StatusBadge status={status} phone={phone} qrBase64={qrBase64} reconnecting={esReconexionGuardada && !falloNecesitaQrManual} desvinculada={sesionDesvinculada} />}
            </div>
            )}
            {status === "connected" && veDesconectar && (
              <button onClick={handleDesconectar} disabled={accion} style={{ padding: "9px 18px", borderRadius: 10, border: `1px solid ${wa.redBorder}`, background: wa.redBg, color: wa.redText, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", opacity: accion ? 0.6 : 1 }}>
                {accion ? "..." : "Desconectar"}
              </button>
            )}
          </div>

          <div style={{ padding: "28px 24px" }}>

            {status === "connected" && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: "24px 0" }}>
                <div style={{ fontSize: 56 }}>✅</div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: wa.green, marginBottom: 6 }}>WhatsApp conectado</div>
                  <div style={{ fontSize: 13.5, color: c.textMuted, fontWeight: 500 }}>
                    Los mensajes que envíes desde el CRM saldrán desde <strong style={{ color: c.textPrimary }}>+{phone}</strong>
                  </div>
                </div>
              </div>
            )}

            {qrBase64 && veVincular && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: c.textPrimary, marginBottom: 6 }}>
                    {needsQrScan ? "Escanea con tu móvil personal (no el de Ana)" : "Escanea el código QR"}
                  </div>
                  <div style={{ fontSize: 13, color: c.textMuted, fontWeight: 500 }}>WhatsApp → Menú → Dispositivos vinculados → Vincular dispositivo</div>
                </div>
                <div style={{ padding: 16, borderRadius: 14, background: "#FFF", border: `2px solid ${wa.greenBorder}`, boxShadow: `0 8px 32px ${wa.greenShadow}` }}>
                  <img src={qrBase64} alt="QR WhatsApp" style={{ width: 240, height: 240, display: "block" }} />
                </div>
                <div style={{ fontSize: 12, color: c.textMuted, textAlign: "center", maxWidth: 360, lineHeight: 1.45 }}>
                  {manualQrMode
                    ? "Escanea este QR y espera en el móvil a «dispositivo vinculado». No pulses otra vez «Generar nuevo QR» mientras el móvil diga «iniciando sesión»."
                    : "El código se renueva solo cada ~20 s. Déjalo en pantalla hasta escanear."}
                </div>
              </div>
            )}

            {anaPhoneConflict && !qrBase64 && veVincular && (
              <div style={{ marginBottom: 16, padding: "14px 16px", borderRadius: 10, background: wa.amberBg, border: `1px solid ${wa.amberBorder}`, fontSize: 13, color: wa.amberText, lineHeight: 1.5 }}>
                {anaWaPhoneReserved ? (
                  <>Has escaneado el WhatsApp de <strong>Ana</strong> (+{anaWaPhoneReserved}). Ese número solo puede usarse en la sesión de Ana, no en la de agente.</>
                ) : (
                  <>Has escaneado el WhatsApp de <strong>Ana</strong>. Ese número no puede usarse también en tu sesión de agente.</>
                )}
                <br /><br />
                Pulsa <strong>«Generar nuevo QR»</strong> y escanea con tu móvil personal de agente (distinto al de Ana).
              </div>
            )}

            {mostrarSpinner && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: "24px 0" }}>
                {esReconexionGuardada ? (
                  <div style={{ marginBottom: 4, padding: "12px 16px", borderRadius: 10, background: wa.amberBg, border: `1px solid ${wa.amberBorder}`, fontSize: 13, color: wa.amberText, lineHeight: 1.5, maxWidth: 420, textAlign: "center" }}>
                    {statusMessage ||
                      "Reconectando tu sesión guardada. No hace falta escanear QR."}
                  </div>
                ) : null}
                <div style={{ width: 48, height: 48, borderRadius: "50%", border: `3px solid ${c.border}`, borderTop: `3px solid ${wa.green}`, animation: "wa-spin 0.9s linear infinite" }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: c.textMuted, textAlign: "center" }}>
                  {qrPreparando
                    ? "Preparando tu código QR…"
                    : esReconexionGuardada
                    ? "Reconectando tu sesión guardada (sin escanear QR)…"
                    : manualQrMode || /eagain_retry|saturado|liberando sesiones/i.test(`${mensajeErrorVisible || ""} ${lastDisconnect || ""}`)
                    ? "Generando QR nuevo (puede liberar otras sesiones del servidor)…"
                    : status === "saved"
                    ? "Recuperando sesión guardada en el servidor..."
                    : "Arrancando Chromium y generando QR..."}
                  {segundosConectando > 0 && ` (${segundosConectando}s)`}
                </div>
                <div style={{ fontSize: 12, color: c.textMuted, textAlign: "center", maxWidth: 360, lineHeight: 1.45 }}>
                  {qrPreparando
                    ? "El código tarda hasta 2 minutos en salir. Espera aquí sin pulsar nada: volver a pulsar reinicia la espera."
                    : conexionAtascada
                    ? esReconexionGuardada
                      ? "Si no conecta, pulsa «Generar nuevo QR» y escanea con el móvil (solo si la reconexión automática falla)."
                      : "Chromium no ha respondido o el QR falló. Usa «Generar nuevo QR» para desbloquear."
                    : esReconexionGuardada
                    ? "Puede tardar hasta 2 minutos. Si no conecta, usa «Generar nuevo QR»."
                    : "Puede tardar hasta 2 minutos. Si no aparece el QR, usa «Generar nuevo QR»."}
                </div>
                {puedeGenerarQr && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", marginTop: 4 }}>
                    {conexionAtascada && (
                      <button
                        type="button"
                        onClick={() => handleConectar({ forzarQR: false })}
                        disabled={accion}
                        style={{ padding: "11px 22px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${wa.greenFrom},${wa.greenTo})`, color: wa.greenText, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700, opacity: accion ? 0.7 : 1 }}
                      >
                        Reintentar
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleNuevoQR}
                      disabled={accion}
                      style={{ padding: "11px 20px", borderRadius: 10, border: `1px solid ${wa.amberBorder}`, background: wa.amberBg, color: wa.amberText, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700, opacity: accion ? 0.7 : 1 }}
                    >
                      Generar nuevo QR
                    </button>
                  </div>
                )}
              </div>
            )}

            {mostrarBotonesReconexion && !mostrarSpinner && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: "16px 0" }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 44, marginBottom: 12 }}>📱</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary, marginBottom: 8 }}>
                    {falloNecesitaQrManual || status === "disconnected" ? "Sesión desconectada" : "Aún no has conectado tu WhatsApp"}
                  </div>
                  <div style={{ fontSize: 13, color: c.textMuted, fontWeight: 500, maxWidth: 400, lineHeight: 1.5 }}>
                    {falloNecesitaQrManual
                      ? "WhatsApp pidió un vínculo nuevo. Pulsa «Generar nuevo QR» y escanea con tu móvil."
                      : status === "disconnected"
                      ? (lastDisconnect === "LOGOUT"
                        ? "Cerraste la sesión desde el móvil o WhatsApp la invalidó. Necesitas un QR nuevo."
                        : "La sesión se cayó (reinicio del servidor, timeout de Chromium o WhatsApp). Intenta reconectar o genera un QR nuevo.")
                      : "Conecta tu teléfono para enviar WhatsApp desde el CRM."}
                  </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center" }}>
                  {!falloNecesitaQrManual && (
                    <button onClick={() => handleConectar({ forzarQR: false })} disabled={accion} style={{ padding: "13px 28px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${wa.greenFrom},${wa.greenTo})`, color: wa.greenText, cursor: "pointer", fontFamily: "inherit", fontSize: 14, fontWeight: 700, boxShadow: `0 4px 14px ${wa.greenShadow}`, opacity: accion ? 0.7 : 1 }}>
                      {accion ? "Iniciando..." : "Conectar WhatsApp"}
                    </button>
                  )}
                  <button onClick={handleNuevoQR} disabled={accion} style={{ padding: "13px 20px", borderRadius: 10, border: `1px solid ${wa.amberBorder}`, background: wa.amberBg, color: wa.amberText, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700, opacity: accion ? 0.7 : 1 }}>
                    Generar nuevo QR
                  </button>
                </div>
              </div>
            )}

            {mensajeErrorVisible && (
              <div style={{ marginTop: 16, padding: "12px 16px", borderRadius: 10, background: wa.redBg, border: `1px solid ${wa.redBorder}`, fontSize: 13, color: wa.redText, fontWeight: 600, lineHeight: 1.45 }}>
                ⚠️ {mensajeErrorVisible}
              </div>
            )}

            <div style={{ marginTop: 20, textAlign: "center" }}>
              <button
                type="button"
                onClick={async () => {
                  const next = !mostrarDiag;
                  setMostrarDiag(next);
                  if (next) await cargarDiagnostico();
                }}
                style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${c.border}`, background: c.bgInput, color: c.textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
              >
                {mostrarDiag ? "Ocultar diagnóstico" : "Ver diagnóstico técnico"}
              </button>
            </div>

            {mostrarDiag && diag && (
              <pre style={{ marginTop: 12, padding: 12, borderRadius: 8, background: c.bgInput, border: `1px solid ${c.border}`, fontSize: 11, color: c.textSecondary, overflow: "auto", maxHeight: 220, textAlign: "left", whiteSpace: "pre-wrap" }}>
                {JSON.stringify(diag, null, 2)}
              </pre>
            )}
          </div>
        </div>

        <div style={{ marginTop: 20, background: c.bgCard, borderRadius: 14, border: `1px solid ${c.border}`, padding: "20px 24px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary, marginBottom: 14 }}>ℹ️ Por qué se desconecta</div>
          {[
            ["🔄", "Reinicio del servidor (Railway)", "Al desplegar o reiniciar, Chromium se apaga. La sesión en disco se recupera sola; si falla, usa «Generar nuevo QR»."],
            ["📱", "Desvinculaste el dispositivo en el móvil", "WhatsApp → Dispositivos vinculados → eliminar CRM. Obliga a escanear QR de nuevo."],
            ["🧠", "Memoria / Chromium", "Si muchos agentes tenían sesión abierta, un reinicio puede saturar el servidor y el QR no arranca a la primera."],
            ["⏱️", "Timeout", "Si en 2 minutos no hay QR, el servidor marca error. Pulsa «Generar nuevo QR»."],
          ].map(([icon, titulo, desc], i, arr) => (
            <div key={titulo} style={{ display: "flex", gap: 14, marginBottom: i < arr.length - 1 ? 14 : 0, paddingBottom: i < arr.length - 1 ? 14 : 0, borderBottom: i < arr.length - 1 ? `1px solid ${c.border}` : "none" }}>
              <div style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, background: wa.greenBg, border: `1px solid ${wa.greenBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>{icon}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary, marginBottom: 3 }}>{titulo}</div>
                <div style={{ fontSize: 12.5, color: c.textMuted, fontWeight: 500, lineHeight: 1.5 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}
